import { useState, useRef, useEffect, useCallback, memo } from 'react';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { CoverageDropdown } from './CoverageDropdown';
import { CoverageIndicator } from './CoverageIndicator';
import { HistoryIconButton } from './HistoryIconButton';
import type { DocType, SectionMetadata, SectionType } from '../../types/document';
import {
  isDesignMetadata,
  isPRDMetadata,
  DESIGN_TYPE_LABELS,
  DESIGN_TYPE_TOOLTIPS,
  PRD_TYPE_LABELS,
  PRD_TYPE_TOOLTIPS,
} from '../../types/document';
import { inferTypeFromAnchor, inferPRDTypeFromAnchor } from '@ceetrix/shared';

interface SectionHeaderProps {
  title: string;
  /** Currently selected coverage anchors (design sections covering PRD anchors) */
  covers?: string[];
  /** All available upstream anchors for coverage dropdown (PRD anchors for design sections) */
  upstreamAnchors?: string[];
  /** Title lookup for upstream anchors (anchor -> section title) */
  upstreamTitleMap?: Record<string, string>;
  /** Anchors not covered by any sibling section (shown with warning icon in dropdown) */
  uncoveredAnchors?: Set<string>;
  metadata?: SectionMetadata;
  docType: DocType;
  isActive?: boolean;
  isTitleEditing?: boolean;
  isCollapsed?: boolean;
  isSectionHovered?: boolean;
  onToggleCollapse?: () => void;
  onDelete: () => void;
  onTitleChange?: (newTitle: string) => void;
  /** Called when coverage selection changes (design sections) */
  onCoversChange?: (covers: string[]) => void;
  onActivateTitle?: () => void;
  onDeactivateTitle?: () => void;
  onNavigateUp?: () => void;
  onNavigateDown?: () => void;
  dragHandleProps?: SyntheticListenerMap;
  /** Story ID for task navigation display (e.g., "229.1") */
  storyId?: string;
  /** Called when user clicks a design capability in PRD coverage indicator */
  onNavigateToDesign?: (anchor: string) => void;
  /** Called when user clicks a task in design coverage indicator */
  onNavigateToTask?: (taskSeq: string) => void;
  /** Called when user clicks a PRD anchor in design covers dropdown */
  onNavigateToPRD?: (anchor: string) => void;
  /** Current section type (Story 254) */
  sectionType?: SectionType | null;
  /** Section anchor for type inference when section_type is null (Story 254 migration) */
  anchor?: string | null;
  /** Called when section type changes (Story 254) */
  onSectionTypeChange?: (type: SectionType) => void;
  /** Called when history button clicked (Story 232) */
  onHistory?: () => void;
  /** Story 429: True when upstream dependency changed since last review */
  isStale?: boolean;
}

export const SectionHeader = memo(function SectionHeader({
  title,
  covers = [],
  upstreamAnchors = [],
  upstreamTitleMap = {},
  uncoveredAnchors = new Set(),
  metadata,
  docType,
  isActive,
  isTitleEditing,
  isCollapsed,
  isSectionHovered,
  onToggleCollapse,
  onDelete,
  onTitleChange,
  onCoversChange,
  onActivateTitle,
  onDeactivateTitle,
  onNavigateUp,
  onNavigateDown,
  dragHandleProps,
  storyId,
  onNavigateToDesign,
  onNavigateToTask,
  onNavigateToPRD,
  sectionType,
  anchor,
  onSectionTypeChange,
  onHistory,
  isStale,
}: SectionHeaderProps) {
  // Story 254/293 migration: infer type from anchor when section_type is null
  // PRD uses requirement/informational, design uses capability/overview
  const effectiveSectionType = sectionType ?? (
    docType === 'prd' ? inferPRDTypeFromAnchor(anchor) : inferTypeFromAnchor(anchor)
  );
  const [editValue, setEditValue] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isTitleEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isTitleEditing]);

  // Update edit value when title prop changes
  useEffect(() => {
    setEditValue(title);
  }, [title]);

  const handleStartEditing = useCallback(() => {
    onActivateTitle?.();
    setEditValue(title);
  }, [title, onActivateTitle]);

  const handleSave = useCallback(() => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== title && onTitleChange) {
      onTitleChange(trimmedValue);
    } else {
      setEditValue(title); // Revert if empty or unchanged
    }
    onDeactivateTitle?.();
  }, [editValue, title, onTitleChange, onDeactivateTitle]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Stop propagation for all arrow keys to prevent drag handler interference
    if (e.key.startsWith('Arrow')) {
      e.stopPropagation();
    }

    if (e.key === 'Enter') {
      // Save and move to body
      const trimmedValue = editValue.trim();
      if (trimmedValue && trimmedValue !== title && onTitleChange) {
        onTitleChange(trimmedValue);
      }
      onNavigateDown?.();
    } else if (e.key === 'Escape') {
      setEditValue(title); // Revert
      onDeactivateTitle?.();
    } else if (e.key === 'ArrowUp') {
      // Navigate to previous section's body
      e.preventDefault();
      const trimmedValue = editValue.trim();
      if (trimmedValue && trimmedValue !== title && onTitleChange) {
        onTitleChange(trimmedValue);
      }
      onNavigateUp?.();
    } else if (e.key === 'ArrowDown') {
      // Navigate to current section's body
      e.preventDefault();
      const trimmedValue = editValue.trim();
      if (trimmedValue && trimmedValue !== title && onTitleChange) {
        onTitleChange(trimmedValue);
      }
      onNavigateDown?.();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // Allow normal cursor movement - don't navigate sections
      // (stopPropagation already called above, no preventDefault needed)
    }
  }, [editValue, title, onTitleChange, onDeactivateTitle, onNavigateUp, onNavigateDown]);

  // Dotted grab lines
  const DOT_SIZE = 1;
  const DOT_GAP = 2;
  const LINE_GAP = 2;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: isActive ? 'var(--bg-muted)' : 'var(--bg-subtle)',
        borderRadius: isCollapsed ? '6px' : '6px 6px 0 0',
      }}
    >
      {/* Title row - wraps RHS to new row when narrow */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '10px 24px',
          position: 'relative',
        }}
      >
        {/* Drag handle - centered dotted grab lines */}
        <div
          {...dragHandleProps}
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '30%',
            top: 0,
            padding: '8px 0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: `${LINE_GAP}px`,
            opacity: isHovered ? 0.5 : 0,
            pointerEvents: isHovered ? 'auto' : 'none',
            transition: 'opacity 150ms ease',
            cursor: 'grab',
          }}
          title="Drag to reorder"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: '100%',
                height: `${DOT_SIZE}px`,
                backgroundImage: `radial-gradient(circle, var(--text-muted, #94a3b8) ${DOT_SIZE}px, transparent ${DOT_SIZE}px)`,
                backgroundSize: `${DOT_SIZE + DOT_GAP}px ${DOT_SIZE}px`,
              }}
            />
          ))}
        </div>

        {/* Collapse toggle arrow - positioned absolutely to not affect title alignment */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            style={{
              position: 'absolute',
              left: '4px',
              top: '50%',
              transform: `translateY(-50%) rotate(${isCollapsed ? '-90deg' : '0deg'})`,
              padding: 0,
              fontSize: '10px',
              color: 'var(--text-muted, #94a3b8)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '24px',
              transition: 'transform 150ms ease, opacity 0.3s ease',
              opacity: isSectionHovered || isActive ? 1 : 0,
            }}
            title={isCollapsed ? 'Expand section' : 'Collapse section'}
          >
            ▼
          </button>
        )}

        {/* Title - editable, minWidth forces RHS to wrap when narrow */}
        {isTitleEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: 700,
              lineHeight: 1.2,
              color: 'var(--text-primary, #1e293b)',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              padding: '0 2px',
              flex: '1 1 300px',
              minWidth: '200px',
            }}
          />
        ) : (
          <h1
            onClick={handleStartEditing}
            style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: 700,
              lineHeight: 1.2,
              color: 'var(--text-primary, #1e293b)',
              cursor: 'text',
              flex: '1 1 300px',
              minWidth: '200px',
            }}
            title="Click to edit title"
          >
            {title}
          </h1>
        )}

        {/* RHS: design type, coverage controls, delete - wraps to new row on narrow screens */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            flex: '0 0 auto',
            position: 'relative',
            zIndex: 1, // Above drag handle overlay
          }}
        >
          {/* Section Type dropdown - Story 254 (design), Story 293 (PRD) */}
          {onSectionTypeChange && (
            <select
              value={effectiveSectionType}
              onChange={(e) => onSectionTypeChange(e.target.value as SectionType)}
              style={{
                padding: '4px 8px',
                fontSize: '13px',
                borderRadius: '4px',
                border: '1px solid var(--border-default, #e2e8f0)',
                backgroundColor: 'var(--bg-base, #fff)',
                color: 'var(--text-primary, #1e293b)',
                cursor: 'pointer',
                opacity: isSectionHovered || isActive ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
              title="Select section type"
            >
              {docType === 'prd' ? (
                <>
                  <option value="informational" title={PRD_TYPE_TOOLTIPS.informational}>
                    {PRD_TYPE_LABELS.informational}
                  </option>
                  <option value="requirement" title={PRD_TYPE_TOOLTIPS.requirement}>
                    {PRD_TYPE_LABELS.requirement}
                  </option>
                  <option value="test_strategy" title={DESIGN_TYPE_TOOLTIPS.test_strategy}>
                    {DESIGN_TYPE_LABELS.test_strategy}
                  </option>
                </>
              ) : (
                <>
                  <option value="overview" title={DESIGN_TYPE_TOOLTIPS.overview}>
                    {DESIGN_TYPE_LABELS.overview}
                  </option>
                  <option value="capability" title={DESIGN_TYPE_TOOLTIPS.capability}>
                    {DESIGN_TYPE_LABELS.capability}
                  </option>
                  <option value="test_strategy" title={DESIGN_TYPE_TOOLTIPS.test_strategy}>
                    {DESIGN_TYPE_LABELS.test_strategy}
                  </option>
                </>
              )}
            </select>
          )}

          {/* Coverage dropdown - for design sections covering PRD requirements */}
          {onCoversChange && upstreamAnchors.length > 0 && (
            <CoverageDropdown
              allAnchors={upstreamAnchors}
              selected={covers}
              uncovered={uncoveredAnchors}
              uncoveredTooltip="Not covered by any design section"
              onChange={onCoversChange}
              onNavigate={onNavigateToPRD}
              label="Implements in PRD"
              anchorTitleMap={upstreamTitleMap}
              visible={isSectionHovered || isActive}
            />
          )}

          {/* Coverage indicators - varies by docType */}
          {docType === 'design' && isDesignMetadata(metadata) && (
            <CoverageIndicator
              count={metadata.taskCount}
              items={metadata.tasks.map((t) => ({
                id: String(t.seq),
                label: t.title,
                sublabel: storyId ? `${storyId}.${t.seq}` : `Task ${t.seq}`,
              }))}
              label="Task Coverage"
              onNavigate={onNavigateToTask}
              visible={isSectionHovered || isActive}
            />
          )}
          {docType === 'prd' && isPRDMetadata(metadata) && (
            <CoverageIndicator
              count={metadata.coveredBy.length}
              items={metadata.coveredBy.map((c) => ({
                id: c.anchor,
                label: c.title,
              }))}
              label="Tech Design Coverage"
              onNavigate={onNavigateToDesign}
              visible={isSectionHovered || isActive}
            />
          )}

          {/* History button (Story 232) */}
          {onHistory && (
            <HistoryIconButton
              onClick={onHistory}
              visible={isSectionHovered || isActive}
            />
          )}

          {/* Delete button */}
          <button
            onClick={onDelete}
            style={{
              padding: '4px 8px',
              fontSize: '14px',
              color: 'var(--text-muted, #94a3b8)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
              opacity: isSectionHovered || isActive ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
            title="Delete section"
          >
            🗑
          </button>

          {/* Story 429: Staleness indicator */}
          {isStale && (
            <span
              title="Upstream dependency changed since last review"
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: 'var(--status-error)',
                marginLeft: '4px',
                flexShrink: 0,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
});
